/**
 * The office's decor library: the chairs, linens, flower arrangements,
 * candelabras, bars, dance floors, chuppahs and lounge furniture it can
 * offer, each with pictures from several angles and the vendors who supply it.
 *
 * A piece belongs to one category. A category says which fields of the look
 * (`DecorLook`) it decides — a gold chiavari chair is `chairStyle: chiavari`,
 * `chairColor: #c9a35e` — and which objects on a floor plan it dresses. A
 * floor plan (or the whole event) picks a piece per category, and any one
 * table, bar or sofa may pick its own (`config.decor` on the plan's object);
 * the realistic 3D view draws what is picked and the AI Render Studio shows
 * the picked pieces' pictures to the model.
 */
export const DECOR_LIBRARY_CATEGORIES = [
    'chairs',
    'linens',
    'napkins',
    'plates',
    'cutlery',
    'glassware',
    'centerpieces',
    'candles',
    'cocktail_tables',
    'bars',
    'dance_floors',
    'chuppahs',
    'lounge',
];
/** Dresses every dining table (round, banquet, head, sweetheart, serpentine…). */
export const DRESSES_TABLES = '@tables';
export const LOUNGE_OBJECT_TYPES = ['SOFA', 'LOVESEAT', 'ARMCHAIR', 'OTTOMAN'];
export const DECOR_LIBRARY_CATEGORY_SPECS = {
    chairs: {
        label: 'Chairs',
        hint: 'Chiavari, cross back, ghost, banquet…',
        fields: ['chairStyle', 'chairColor', 'cushionColor'],
        dresses: [DRESSES_TABLES, 'CHAIR_ROW'],
    },
    linens: {
        label: 'Table linens',
        hint: 'Table cloths — plain, satin, sequin, velvet',
        fields: ['linenColor', 'linenFinish'],
        dresses: [DRESSES_TABLES],
    },
    napkins: {
        label: 'Napkins',
        hint: 'Colour and fold',
        fields: ['napkinColor', 'napkinFold'],
        dresses: [DRESSES_TABLES],
    },
    plates: {
        label: 'Plates & chargers',
        hint: 'Charger plates and china',
        fields: ['charger', 'chargerColor', 'plateColor'],
        dresses: [DRESSES_TABLES],
    },
    cutlery: {
        label: 'Cutlery',
        hint: 'Silver, gold, black flatware',
        fields: ['cutlery'],
        dresses: [DRESSES_TABLES],
    },
    glassware: {
        label: 'Glassware',
        hint: 'Wine and water glasses',
        fields: ['glass', 'glassColor', 'glassShape'],
        dresses: [DRESSES_TABLES],
    },
    centerpieces: {
        label: 'Flower arrangements',
        hint: 'Centrepieces — low, tall, candles only',
        fields: ['centerpiece', 'flowerColors', 'greeneryColor'],
        dresses: [DRESSES_TABLES, 'COCKTAIL_TABLE'],
    },
    candles: {
        label: 'Candelabras & candles',
        hint: 'Taper candlesticks, candelabras, votives',
        fields: ['candleStyle', 'candleFinish'],
        dresses: [DRESSES_TABLES, 'COCKTAIL_TABLE'],
    },
    cocktail_tables: {
        label: 'Cocktail tables',
        hint: 'Highboy covers — spandex, draped',
        fields: ['cocktailStyle', 'cocktailColor'],
        dresses: ['COCKTAIL_TABLE'],
    },
    bars: {
        label: 'Bars',
        hint: 'Wood, lacquer, marble, mirror, lit',
        fields: ['barStyle', 'barColor'],
        dresses: ['BAR'],
    },
    dance_floors: {
        label: 'Dance floors',
        hint: 'Black & white, gloss, parquet, LED',
        fields: ['danceFloorStyle'],
        dresses: ['DANCE_FLOOR'],
    },
    chuppahs: {
        label: 'Chuppahs & arches',
        hint: 'Floral canopy, draped, birch, modern',
        fields: ['chuppahStyle', 'chuppahFabricColor', 'chuppahFlowerColors'],
        dresses: ['CHUPPAH', 'ARCH'],
    },
    lounge: {
        label: 'Lounge furniture',
        hint: 'Sofas, loveseats, armchairs, ottomans',
        fields: ['loungeStyle', 'loungeColor'],
        dresses: [...LOUNGE_OBJECT_TYPES],
    },
};
/** The record a library piece's pictures are filed on (office only). */
export const DECOR_LIBRARY_ENTITY_TYPE = 'DECOR_ITEM';
/** `documentType` of a library piece's pictures. */
export const DECOR_LIBRARY_DOCUMENT_TYPE = 'DECOR_LIBRARY';
/** How many pictures (angles) one piece holds. */
export const MAX_DECOR_LIBRARY_PICTURES = 12;
/** How many vendors one piece names. */
export const MAX_DECOR_LIBRARY_VENDORS = 20;
/** How many tags one piece carries. */
export const MAX_DECOR_LIBRARY_TAGS = 20;
/** How many pictures one AI read of a piece looks at — the AI client takes five. */
export const MAX_DECOR_LIBRARY_READ_PICTURES = 5;
/** How many picked pieces' pictures one AI render shows the model. */
export const MAX_RENDER_LIBRARY_PICTURES = 6;
/** Colour families the library is filtered by. */
export const DECOR_COLOR_FAMILIES = [
    'white',
    'ivory',
    'grey',
    'black',
    'gold',
    'pink',
    'red',
    'orange',
    'yellow',
    'green',
    'blue',
    'purple',
    'brown',
];
export const DECOR_COLOR_FAMILY_SWATCH = {
    white: '#ffffff',
    ivory: '#efe6d2',
    grey: '#9ca3af',
    black: '#1f1f22',
    gold: '#c9a35e',
    pink: '#f2b6c6',
    red: '#c0392b',
    orange: '#e67e22',
    yellow: '#f1c40f',
    green: '#6f8662',
    blue: '#4a78b5',
    purple: '#8e6bbf',
    brown: '#7a5232',
};
//# sourceMappingURL=decor-library.js.map